import React, { useEffect, useState } from "react";
import { Loader2, Filter, X, Download, FileText, Users, ListChecks, Layers } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../../lib/api";
import { downloadFile } from "../../../lib/download";
import { gbp } from "../../../lib/constants";

export default function AdminReports() {
  const [tab, setTab] = useState("aggregated");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [agg, setAgg] = useState(null);
  const [cust, setCust] = useState(null);
  const [grouped, setGrouped] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = () => {
    const p = {};
    if (start) p.start_date = start;
    if (end) p.end_date = end;
    return p;
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.get("/admin/reports/aggregated", { params: params() }),
      adminApi.get("/admin/reports/customer-wise", { params: params() }),
      adminApi.get("/admin/reports/grouped", { params: params() }),
    ]).then(([a, c, g]) => { setAgg(a.data); setCust(c.data); setGrouped(g.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => load(), []);

  const doExport = async (report_type, format) => {
    const q = new URLSearchParams({ report_type, format, ...params() }).toString();
    try {
      await downloadFile(`/admin/reports/export?${q}`, `${report_type}-report.${format}`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const fmt = (d) => new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

  const TABS = [
    { key: "aggregated", label: "Shopping List", icon: ListChecks },
    { key: "customer", label: "Customer-Wise", icon: Users },
    { key: "grouped", label: "By Category", icon: Layers },
  ];

  return (
    <div>
      {/* Filter + summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Start date</label>
          <input data-testid="report-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 px-3 rounded-md border border-gray-200 text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">End date</label>
          <input data-testid="report-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 px-3 rounded-md border border-gray-200 text-sm" />
        </div>
        <button data-testid="report-apply-filter" onClick={load} className="h-10 px-4 rounded-md bg-[#228B22] hover:bg-[#1A4331] text-white text-sm font-semibold flex items-center gap-2 transition-colors"><Filter size={15} /> Apply</button>
        <button data-testid="report-clear-filter" onClick={() => { setStart(""); setEnd(""); setTimeout(load, 0); }} className="h-10 px-4 rounded-md border border-gray-300 text-sm font-semibold text-gray-600 flex items-center gap-2 hover:bg-gray-50 transition-colors"><X size={15} /> Clear</button>
      </div>

      {agg && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <SummaryCard label="Subtotal" value={gbp(agg.summary.subtotal)} />
          <SummaryCard label="Fixed Charges" value={gbp(agg.summary.fixed_charges)} />
          <SummaryCard label="Delivery Charges" value={gbp(agg.summary.delivery_charges)} />
          <SummaryCard label="Grand Total" value={gbp(agg.summary.grand_total)} highlight />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`report-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#228B22] text-[#1A4331]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#228B22]" size={28} /></div>
      ) : (
        <>
          {tab === "aggregated" && agg && (
            <div>
              <div className="flex justify-end gap-2 mb-3">
                <ExportBtn onClick={() => doExport("aggregated", "csv")} label="CSV" testid="export-agg-csv" />
                <ExportBtn onClick={() => doExport("aggregated", "pdf")} label="PDF" pdf testid="export-agg-pdf" />
              </div>
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-left">
                    <tr><th className="px-4 py-3 font-semibold">Product</th><th className="px-4 py-3 font-semibold">Unit</th><th className="px-4 py-3 font-semibold">Qty</th><th className="px-4 py-3 font-semibold">Unit Price</th><th className="px-4 py-3 font-semibold text-right">Total</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agg.products.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No data for this range.</td></tr>
                    ) : agg.products.map((p, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-medium text-[#0A1F13]">{p.product_name}</td>
                        <td className="px-4 py-2.5 text-gray-500">{p.unit}</td>
                        <td className="px-4 py-2.5">{p.total_quantity}</td>
                        <td className="px-4 py-2.5">{gbp(p.unit_price)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{gbp(p.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "customer" && cust && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-500">{cust.total_customers} orders</p>
                <div className="flex gap-2">
                  <ExportBtn onClick={() => doExport("customer_wise", "csv")} label="CSV" testid="export-cust-csv" />
                  <ExportBtn onClick={() => doExport("customer_wise", "pdf")} label="PDF" pdf testid="export-cust-pdf" />
                </div>
              </div>
              <div className="space-y-3">
                {cust.orders.map((o, idx) => (
                  <div key={o.id} className="bg-white rounded-lg border border-gray-200 p-4 text-sm">
                    <p className="font-bold text-[#0A1F13]">Order #{idx + 1} — {o.delivery_name}</p>
                    <p className="text-gray-500 mt-1">📅 {fmt(o.created_at)}</p>
                    <p className="text-gray-500">📞 {o.delivery_phone}</p>
                    <p className="text-gray-500">📍 {o.delivery_address}, {o.delivery_postcode}</p>
                    <p className="text-gray-500">🚚 {o.delivery_zone}</p>
                    <table className="w-full mt-2 border-t border-gray-100 pt-2">
                      <tbody>
                        {o.items.map((it, i) => (
                          <tr key={i}><td className="py-1 text-gray-600">{it.product_name} × {it.quantity}</td><td className="py-1 text-right">{gbp(it.subtotal)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex flex-wrap gap-x-5 border-t border-gray-100 pt-2 mt-1 text-gray-500">
                      <span>Subtotal: <b className="text-[#0A1F13]">{gbp(o.subtotal)}</b></span>
                      <span>Fixed: <b className="text-[#0A1F13]">{gbp(o.fixed_charge)}</b></span>
                      <span>Delivery: <b className="text-[#0A1F13]">{gbp(o.outside_dorchester_charge)}</b></span>
                      <span className="ml-auto">Total: <b className="text-[#228B22]">{gbp(o.total_amount)}</b></span>
                    </div>
                  </div>
                ))}
                {cust.orders.length === 0 && <p className="text-center text-gray-400 py-10">No orders in range.</p>}
              </div>
            </div>
          )}

          {tab === "grouped" && grouped && (
            <div className="space-y-4">
              {grouped.categories.length > 0 && (
                <div className="flex justify-end gap-2">
                  <ExportBtn onClick={() => doExport("grouped", "csv")} label="CSV" testid="export-grouped-csv" />
                  <ExportBtn onClick={() => doExport("grouped", "pdf")} label="PDF" pdf testid="export-grouped-pdf" />
                </div>
              )}
              {grouped.categories.map((c) => (
                <div key={c.category} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-[#E8F5E9] px-4 py-2.5 flex justify-between items-center">
                    <span className="font-bold text-[#1A4331]">{c.category}</span>
                    <span className="text-sm font-semibold text-[#166534]">{gbp(c.category_total)}</span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      {c.products.map((p, i) => (
                        <tr key={i}><td className="px-4 py-2 text-gray-700">{p.name} × {p.quantity}</td><td className="px-4 py-2 text-right font-medium">{gbp(p.total)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {grouped.categories.length === 0 ? (
                <p className="text-center text-gray-400 py-10">No data in range.</p>
              ) : (
                <div className="flex justify-end text-lg font-bold text-[#0A1F13]">Grand Total: <span className="text-[#228B22] ml-2">{gbp(grouped.grand_total)}</span></div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "bg-[#1A4331] text-white border-[#1A4331]" : "bg-white border-gray-200"}`}>
      <p className={`text-xs ${highlight ? "text-white/70" : "text-gray-500"}`}>{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

function ExportBtn({ onClick, label, pdf, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex items-center gap-1.5 px-3 h-9 rounded-md border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
      {pdf ? <FileText size={14} /> : <Download size={14} />} {label}
    </button>
  );
}
